import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [emailCaptures, setEmailCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exportLoading, setExportLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    // Check if we already have valid credentials in sessionStorage
    const savedCredentials = sessionStorage.getItem('dashboard_credentials');
    if (savedCredentials) {
      const creds = JSON.parse(savedCredentials);
      setCredentials(creds);
      fetchDataWithCredentials(creds);
    } else {
      setShowLogin(true);
      setLoading(false);
    }
  }, []);

  const createAuthenticatedAxios = (creds) => {
    return axios.create({
      auth: {
        username: creds.username,
        password: creds.password
      }
    });
  };

  const handleLogin = async (username, password) => {
    const creds = { username, password };
    const authenticatedAxios = createAuthenticatedAxios(creds);
    
    try {
      // Test authentication with a simple request
      await authenticatedAxios.get(`${API}/analytics/summary`);
      
      // If successful, save credentials and load dashboard
      setCredentials(creds);
      sessionStorage.setItem('dashboard_credentials', JSON.stringify(creds));
      setShowLogin(false);
      setAuthError(false);
      fetchDataWithCredentials(creds);
    } catch (error) {
      if (error.response?.status === 401) {
        setAuthError(true);
      } else {
        console.error('Error during authentication:', error);
      }
    }
  };

  const fetchDataWithCredentials = (creds) => {
    const authenticatedAxios = createAuthenticatedAxios(creds);
    fetchAnalytics(authenticatedAxios);
    fetchEmailCaptures(authenticatedAxios);
  };

  const fetchAnalytics = async (authenticatedAxios) => {
    try {
      const response = await authenticatedAxios.get(`${API}/analytics/summary`);
      setAnalytics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      handleAuthError(error);
      setLoading(false);
    }
  };

  const fetchEmailCaptures = async (authenticatedAxios) => {
    try {
      const response = await authenticatedAxios.get(`${API}/analytics/email-captures`);
      setEmailCaptures(response.data.captures);
    } catch (error) {
      console.error('Error fetching email captures:', error);
      handleAuthError(error);
    }
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      // Clear invalid credentials and show login again
      sessionStorage.removeItem('dashboard_credentials');
      setCredentials(null);
      setShowLogin(true);
      setAuthError(true);
    }
  };

  const exportEmailCaptures = async () => {
    if (!credentials) return;
    setExportLoading(true);
    const authenticatedAxios = createAuthenticatedAxios(credentials);
    try {
      const response = await authenticatedAxios.get(`${API}/analytics/export-email-captures`);
      
      // Convert to CSV and download
      const csvData = convertToCSV(response.data.data);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `email_captures_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting email captures:', error);
      handleAuthError(error);
    }
    setExportLoading(false);
  };

  const exportIndustryData = async (industry) => {
    if (!credentials) return;
    setExportLoading(true);
    const authenticatedAxios = createAuthenticatedAxios(credentials);
    try {
      const response = await authenticatedAxios.get(`${API}/analytics/export/${industry}`);
      
      // Convert to CSV and download
      const csvData = convertToCSV(response.data.data);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `workplace_wellbeing_data_${industry}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
    setExportLoading(false);
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    const csvRows = data.map(row => 
      headers.map(header => `"${row[header] || ''}"`).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Login Form Component
  const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoginLoading(true);
      await handleLogin(username, password);
      setLoginLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-12 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Dashboard Access</h1>
            <p className="text-slate-600">
              Please enter your credentials to access the analytics dashboard.
            </p>
          </div>
          
          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">
                ❌ Invalid credentials. Please try again.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter username"
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter password"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50"
              style={{ background: '#333333' }}
            >
              {loginLoading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>
          
          <div className="mt-8 bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-600">
              🔒 This dashboard contains sensitive personal data and is protected for GDPR compliance.
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showLogin) {
    return <LoginForm />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">
            Kalobrate Analytics Dashboard
          </h1>
          <p className="text-slate-600">
            Track workplace wellbeing calculator submissions and email captures
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('email-captures')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email-captures'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Email Captures ({analytics?.email_requests || 0})
            </button>
            <button
              onClick={() => setActiveTab('industry')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'industry'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Segments Analysis
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600">Total Submissions</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {analytics.total_calculations}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600">Email Captures</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {analytics.email_requests}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="bg-green-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600">Conversion Rate</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {(analytics.conversion_rate || 0).toFixed(1)}%
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="bg-purple-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600">Recent (30 days)</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {analytics.recent_submissions}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="bg-orange-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator Breakdown */}
            <div className="bg-white rounded-xl shadow-lg p-8 mt-6">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Calculator Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 rounded-full p-2 mr-3">
                      <span className="text-blue-600 font-semibold">🏢</span>
                    </div>
                    <h4 className="text-lg font-semibold text-blue-900">Culture Calculator</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-700">Submissions:</span>
                      <span className="font-semibold text-blue-900">{analytics.culture_calculations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-700">Email Captures:</span>
                      <span className="font-semibold text-blue-900">{analytics.culture_emails}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-700">Total Estimated Cost:</span>
                      <span className="font-semibold text-blue-900">{formatCurrency(analytics.culture_cost || 0)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 rounded-full p-2 mr-3">
                      <span className="text-green-600 font-semibold">⚡</span>
                    </div>
                    <h4 className="text-lg font-semibold text-green-900">Productivity Calculator</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Submissions:</span>
                      <span className="font-semibold text-green-900">{analytics.productivity_calculations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Email Captures:</span>
                      <span className="font-semibold text-green-900">{analytics.productivity_emails}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Total Productivity Drag:</span>
                      <span className="font-semibold text-green-900">{formatCurrency(analytics.productivity_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Avg Fragility Score:</span>
                      <span className="font-semibent text-green-900">{Math.round(analytics.avg_fragility_score || 0)}/100</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Combined Cost Statistics */}
            <div className="bg-white rounded-xl shadow-lg p-8 mt-6">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Combined Cost Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-2">Total Estimated Annual Impact</p>
                  <p className="text-3xl font-bold text-red-600">
                    {formatCurrency(analytics.total_estimated_cost || 0)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Culture + Productivity</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-2">Conversion Rate</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {(analytics.conversion_rate || 0).toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Submissions → Email Captures</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-2">Average Team Size</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {Math.round(((analytics.avg_employees || 0) + (analytics.avg_headcount || 0)) / 2)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Across both calculators</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Segments Tab - Industries & Pressure Types */}
        {activeTab === 'industry' && analytics && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">Segments Breakdown</h3>
                <button
                  onClick={() => exportIndustryData('all')}
                  disabled={exportLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {exportLoading ? 'Exporting...' : 'Export All Data'}
                </button>
              </div>
              
              <p className="text-slate-600 mb-6">
                Analysis by culture industries and productivity pressure types
              </p>
              
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Segment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Calculator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Submissions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Avg Cost
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Total Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {analytics.segments && analytics.segments.map((segment, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {segment.segment}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            segment.segment_type === 'industry' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {segment.segment_type === 'industry' ? 'Industry' : 'Pressure Type'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            segment.calculator_type === 'culture' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {segment.calculator_type === 'culture' ? '🏢 Culture' : '⚡ Productivity'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {segment.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {formatCurrency(segment.avg_cost || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                          {formatCurrency(segment.total_cost || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Email Captures Tab */}
        {activeTab === 'email-captures' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">Email Capture Requests</h3>
                <button
                  onClick={exportEmailCaptures}
                  disabled={exportLoading || emailCaptures.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
              
              {emailCaptures.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">No email capture requests yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Industry
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Employees
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Estimated Cost
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {emailCaptures.map((capture, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {formatDate(capture.timestamp)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">
                              {capture.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {capture.company_name || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900 capitalize">
                              {capture.industry || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {capture.employees || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {capture.estimated_cost ? formatCurrency(capture.estimated_cost) : '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;